import React, { Suspense } from 'react'
import { useLingui } from '@lingui/react/macro'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { useParams, useSearchParams } from 'react-router-dom'

import { YStack } from 'tamagui'

import { getSlugFromPrettyUrl } from '@data/util/urlHelpers'

import { EventMetadata } from '@newComponents/Event/EventMetadata'
import { Card } from '@newComponents/Card'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { Spinner } from '@newComponents/Spinner'

import { EventItem } from '@newComponents/Event/EventItem'
import { lazy as loadable } from '@loadable/component'

import { EventAttendContent } from './EventAttendContent'

import { EventAttendScreenQuery } from './__generated__/EventAttendScreenQuery.graphql'

const EventAttendFormDialog = loadable(
  () => import('../../modals/EventAttendForm/EventAttendFormDialog')
)

export const EventAttendScreen = () => {
  const { t } = useLingui()
  const params = useParams<'eventId'>()
  const [searchParams] = useSearchParams()

  const data = useLazyLoadQuery<EventAttendScreenQuery>(
    graphql`
      query EventAttendScreenQuery($slug: String!) {
        event: eventBySlug(slug: $slug) {
          name

          # Screen-level fields for page title and conditional rendering
          eventTicketingTiers(orderBy: [SORT_ORDER_ASC, CREATED_AT_ASC]) {
            totalCount
          }
          myAttendance {
            visibleStatus
            paymentStatus
            formSubmission {
              id
            }
          }
          myTicketOrder {
            status
          }
          attendeeForm {
            rowId
          }

          ...EventItemNew_event
          ...EventAttendContent_event
          ...EventMetadata_event
        }

        ...EventItemNew_query
        ...EventAttendContent_query
      }
    `,
    { slug: getSlugFromPrettyUrl(params.eventId) }
  )

  const statusParam = searchParams.get('status')

  if (!data.event) {
    return null
  }

  // Attendance forms come AFTER the ticket purchase on ticketed events:
  // tier selection and checkout run first (EventAttendContent), and the form
  // shows once payment has succeeded — or, on approval-gated events, once a
  // hold has been authorized (REQUIRES_CAPTURE) — until it has been submitted.
  // On non-ticketed events the form gates attendance, so it shows while the
  // user has no active attendance.
  const hasPaidOrHeldTicket =
    data.event.myAttendance?.paymentStatus === 'SUCCEEDED' ||
    data.event.myTicketOrder?.status === 'REQUIRES_CAPTURE'

  const needsAttendeeForm =
    !!data.event.attendeeForm &&
    statusParam !== 'NOT_ATTENDING' &&
    (data.event.eventTicketingTiers.totalCount > 0
      ? hasPaidOrHeldTicket && !data.event.myAttendance?.formSubmission
      : !data.event.myAttendance ||
        data.event.myAttendance.visibleStatus === 'NOT_ATTENDING')

  const pageTitle =
    data.event.eventTicketingTiers.totalCount > 0 &&
    data.event.myAttendance?.paymentStatus !== 'SUCCEEDED'
      ? t`Confirm Attendance and Select Tickets for ${data.event.name}`
      : t`Confirm Attendance for ${data.event.name}`

  return (
    <>
      <EventMetadata title={pageTitle} event={data.event} />

      <GradientBackgroundScreen alignItems='center' gap='$8'>
        <EventItem
          event={data.event}
          query={data}
          maxWidth={600}
          width='100%'
          showAttendButton={false}
          displayFullDate
          displayVisibility={false}
        />

        {needsAttendeeForm ? (
          <Card gap='$6' maxWidth={600} width='100%'>
            <Suspense
              fallback={
                <YStack flex={1} alignItems='center' justifyContent='center'>
                  <Spinner size='$9' color='$primaryColor' />
                </YStack>
              }
            >
              <EventAttendFormDialog />
            </Suspense>
          </Card>
        ) : (
          <EventAttendContent
            event={data.event}
            query={data}
            maxWidth={600}
            width='100%'
          />
        )}
      </GradientBackgroundScreen>
    </>
  )
}

export default EventAttendScreen
